import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import {
  motion,
  useMotionValue,
  useTransform,
  useMotionValueEvent,
  useAnimationFrame,
  animate,
  useReducedMotion,
} from "framer-motion";
import { Reveal } from "@/components/util/Reveal";
import { useSettings } from "@/lib/settings";
import {
  FeaturedCardMedia,
  CarouselPauseButton,
  autoplayAllowed,
  useCarouselPause,
  useInView,
} from "@/components/home/FeaturedCardMedia";

// Continuous desktop track (Sep 2026, owner-approved architecture).
// Single tuning dial for steady-state travel speed in px/sec —
// approved test values: 30 (gallery-calm) / 36 (start) / 44 (more alive).
const TRACK_SPEED = 36;

/**
 * Mobile-only 3D Cover Flow carousel for the homepage Featured
 * Projects section. Reuses the same CMS `projects` array as the
 * desktop track — single source of truth. Built on the already-
 * installed `framer-motion` package. No new dependency.
 *
 * ------------------------------------------------------------------
 * Interaction model (single continuous variable)
 * ------------------------------------------------------------------
 * A single `useMotionValue` called `position` — measured in card-step
 * units — represents the *fractional index* that is currently
 * centered. This drives EVERY card's transforms (x, translateZ,
 * rotateY, scale, opacity, zIndex) continuously. Drag and snap both
 * write to `position`, so there is never a discrete "pose switch" at
 * release — the geometry stays connected to the finger throughout.
 *
 *   During drag: position = startPosition - pointerΔ / step
 *      (swiping LEFT reduces pointerΔ.x  → position INCREASES  → next
 *       project scrolls in from the right and becomes centered.)
 *   On release : animate(position, nearest integer with momentum bias)
 *   Pagination : displayActive = wrap(round(position.get()), N)
 *
 * ------------------------------------------------------------------
 * Infinite loop
 * ------------------------------------------------------------------
 * `position` is unbounded — it can drift beyond [0, N-1] in either
 * direction. Each card's raw offset (index - position) is wrapped to
 * the shortest signed distance around the ring:
 *
 *   let o = ((raw % N) + N) % N;
 *   if (o > N / 2) o -= N;
 *
 * The wrap discontinuity happens at |offset| = N/2, which for N=7 is
 * 3.5 — far beyond the visible region (~1.5). Cards there are fully
 * transparent (opacity clamped to 0 past |offset| = 2), so the wrap
 * is completely invisible. Result: 1→2→3→…→N→1→2 forward + reverse,
 * no visible reset, no rewind animation.
 *
 * ------------------------------------------------------------------
 * 3D geometry (all values scale with rendered container width W)
 * ------------------------------------------------------------------
 *   step         = 0.70 * W          (horizontal spacing between cards)
 *   cardW        = min(280, 0.68 W)  (active card width, capped)
 *   x(offset)    = clamp(offset, -1.5, 1.5) * step
 *   rotateY      = -clamp(offset, -1.5, 1.5) * 30°
 *   translateZ   = -min(|offset|, 2) * 120  (side cards recede)
 *   scale        = 1 - min(|offset|, 2) * 0.14
 *   opacity      = max(0, 1 - min(|offset|, 2) * 0.5)
 *   zIndex       = round(100 - |offset| * 20)
 *
 * At 390 wide → step=273, cardW=265, side card scaled ≈ 228.
 *   active right edge ≈ 132px; nearest side card left edge ≈ 159px
 *   → ~27px of visible visual separation between center and neighbor.
 */
const wrap = (n, N) => ((n % N) + N) % N;

const CoverFlowCard = ({ project, index, position, step, cardW, cardH, N, media }) => {
  // Signed shortest distance around the ring.
  const offset = useTransform(position, (p) => {
    const raw = index - p;
    let o = ((raw % N) + N) % N;
    if (o > N / 2) o -= N;
    return o;
  });
  const x = useTransform(offset, (o) => o * step);
  const z = useTransform(offset, (o) => -Math.min(Math.abs(o), 2) * 120);
  const rotateY = useTransform(offset, (o) => -Math.max(-1.5, Math.min(1.5, o)) * 30);
  const scale = useTransform(offset, (o) => Math.max(0.55, 1 - Math.min(Math.abs(o), 2) * 0.14));
  const opacity = useTransform(offset, (o) => Math.max(0, 1 - Math.min(Math.abs(o), 2) * 0.55));
  const zIndex = useTransform(offset, (o) => Math.round(100 - Math.abs(o) * 20));
  const pointerEvents = useTransform(offset, (o) => (Math.abs(o) < 0.5 ? "auto" : "none"));

  return (
    <motion.div
      data-testid={`portfolio-cover-card-${project.id}`}
      style={{
        x,
        z,
        rotateY,
        scale,
        opacity,
        zIndex,
        pointerEvents,
        width: cardW,
        height: cardH,
        left: `calc(50% - ${cardW / 2}px)`,
        transformStyle: "preserve-3d",
      }}
      className="absolute top-0 will-change-transform"
    >
      <Link
        to={`/portfolio/${project.slug || project.id}`}
        className="ur-card block h-full overflow-hidden hover:border-[#FF6600]/40"
      >
        <FeaturedCardMedia
          project={project}
          {...media}
          alt={project.title}
          imgProps={{ loading: "lazy" }}
          testid={`portfolio-cover-media-${project.id}`}
        />
        <div className="p-3">
          <div className="font-semibold text-white truncate text-sm">{project.title}</div>
          <div className="text-[11px] text-[#A3A3A3] mt-0.5 truncate">{project.category}</div>
        </div>
      </Link>
    </motion.div>
  );
};

const PortfolioCoverFlow = ({ projects, paused, onTogglePause }) => {
  const [containerW, setContainerW] = useState(360);
  const [displayActive, setDisplayActive] = useState(0);
  const containerRef = useRef(null);
  const position = useMotionValue(0);
  const animRef = useRef(null);
  const reduce = useReducedMotion();
  const N = projects.length;

  // Gesture-intent classification. `drag="x"` on framer's <motion.div>
  // captures every horizontal component of a gesture — even a mostly-
  // vertical scroll with tiny horizontal drift on iOS Safari. That
  // wobbled the cards during vertical page scroll. We replace it with
  // native PointerEvents + an intent lock: the first ~8px of movement
  // decides horizontal-vs-vertical; only clear horizontal drags
  // control the carousel. Diagonal / vertical-biased gestures are
  // ignored so the page scrolls natively over the cards.
  const gestureRef = useRef({
    pointerId: null,
    startX: 0,
    startY: 0,
    startPos: 0,
    intent: "undecided", // "undecided" | "horizontal" | "vertical"
    lastX: 0,
    lastT: 0,
    velocity: 0,
  });
  const GESTURE_THRESHOLD = 8;   // px before intent is decided
  const HORIZONTAL_BIAS = 1.2;   // |dx| must beat |dy| by this ratio to lock horizontal

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0) setContainerW(rect.width);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Video covers: every visible card plays; the section must be near/in the viewport.
  const sectionNear = useInView(containerRef, { rootMargin: "300px 0px" });
  const sectionVisible = useInView(containerRef);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const media = { rootRef: containerRef, near: sectionNear, visible: sectionVisible, paused };
  const [allowVideo] = useState(autoplayAllowed);
  const showPause = allowVideo && projects.some((p) => p.homepage_video_cover);

  useMotionValueEvent(position, "change", (v) => {
    if (N > 0) setDisplayActive(wrap(Math.round(v), N));
  });

  // One-time discovery hint (Sep 2026, owner-approved): once the carousel
  // has been ~40% visible for ~1.75s, glide position base→base+0.26→base
  // once (≈0.8s, ease-out then ease-in-out — no spring/bounce) to quietly
  // communicate "you can swipe this". ANY user interaction (pointerdown on
  // the drag surface, pagination click) cancels it permanently; a mid-
  // glide cancel settles to the nearest integer via goto() so control
  // returns cleanly. Never armed under prefers-reduced-motion, never when
  // there is only one project. At most one hint per page load; no
  // autoplay, no interval.
  const hintAnimRef = useRef(null);
  const hintStateRef = useRef({ interacted: false, fired: false, timer: null });

  useEffect(() => {
    const el = containerRef.current;
    if (!el || reduce || N < 2) return;
    const st = hintStateRef.current;
    const cancelTimer = () => {
      if (st.timer) { clearTimeout(st.timer); st.timer = null; }
    };
    const fire = () => {
      if (st.interacted || st.fired || pausedRef.current) return;
      st.fired = true;
      const base = position.get();
      hintAnimRef.current = animate(position, [base, base + 0.26, base], {
        duration: 0.8,
        times: [0, 0.55, 1],
        ease: ["easeOut", "easeInOut"],
      });
    };
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (st.interacted || st.fired) { io.disconnect(); return; }
          // Arm only while meaningfully visible; scrolling away cancels
          // the pending timer and re-arms on re-entry — the hint never
          // fires off-screen and never fires twice.
          if (e.isIntersecting) {
            cancelTimer();
            st.timer = setTimeout(fire, 1750);
          } else {
            cancelTimer();
          }
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => {
      if (st.timer) clearTimeout(st.timer);
      io.disconnect();
      hintAnimRef.current?.stop();
    };
  }, [reduce, N]);

  // User control always wins. Called from the earliest interaction
  // signals (pointerdown, pagination click) — before any gesture math.
  const cancelDiscoveryHint = () => {
    const st = hintStateRef.current;
    st.interacted = true;
    if (st.timer) { clearTimeout(st.timer); st.timer = null; }
    if (hintAnimRef.current) {
      hintAnimRef.current = null;
      // Instant, exact settle: jump() stops the hint's animation on the
      // motion value AND lands on the nearest integer in the same frame.
      // A spring/rAF settle was rejected after verification: it can be
      // killed by onPointerDown's own animRef.stop() (frozen mid-glide)
      // and it moves cards under the user's finger (wrong-card taps).
      position.jump(Math.round(position.get()));
    }
  };

  const step = containerW * 0.7;
  const cardW = Math.min(280, containerW * 0.68);
  const cardH = Math.round(cardW * 0.75) + 54;

  const goto = (targetPos) => {
    animRef.current?.stop();
    animRef.current = animate(
      position,
      targetPos,
      reduce
        ? { duration: 0 }
        : { type: "spring", stiffness: 380, damping: 38, mass: 0.7, restDelta: 0.001 },
    );
  };

  const dotClick = (i) => {
    cancelDiscoveryHint();
    const curP = Math.round(position.get());
    const curActive = wrap(curP, N);
    let delta = i - curActive;
    if (delta > N / 2) delta -= N;
    else if (delta < -N / 2) delta += N;
    goto(curP + delta);
  };

  const onPointerDown = (e) => {
    cancelDiscoveryHint();
    // Only primary pointer / no ongoing gesture.
    if (gestureRef.current.pointerId != null) return;
    animRef.current?.stop();
    gestureRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startPos: position.get(),
      intent: "undecided",
      lastX: e.clientX,
      lastT: performance.now(),
      velocity: 0,
    };
  };

  const onPointerMove = (e) => {
    const g = gestureRef.current;
    if (g.pointerId == null || e.pointerId !== g.pointerId) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;

    if (g.intent === "undecided") {
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      if (absX < GESTURE_THRESHOLD && absY < GESTURE_THRESHOLD) return;
      // Bias toward vertical scroll: horizontal must clearly dominate.
      if (absX > absY * HORIZONTAL_BIAS) {
        g.intent = "horizontal";
        // Capture subsequent moves even if pointer leaves the element.
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
      } else {
        g.intent = "vertical";
        return; // page will scroll natively; carousel stays put
      }
    }
    if (g.intent === "vertical") return;

    // Horizontal — drive position continuously. `preventDefault` blocks
    // browser text-selection / horizontal pan while we're in control.
    position.set(g.startPos - dx / step);
    // Track velocity for release snap.
    const now = performance.now();
    if (now - g.lastT > 15) {
      g.velocity = (e.clientX - g.lastX) / (now - g.lastT); // px per ms
      g.lastX = e.clientX;
      g.lastT = now;
    }
    if (e.cancelable && typeof e.preventDefault === "function") e.preventDefault();
  };

  const finishGesture = (settled) => {
    const g = gestureRef.current;
    if (g.intent === "horizontal") {
      const curP = position.get();
      const dx = curP - g.startPos; // in step units, signed
      const vx = g.velocity;        // px/ms. Negative → user was moving finger left.
      let target;
      // Momentum wins on a fast flick.
      if (vx < -0.5) target = Math.ceil(curP);
      else if (vx > 0.5) target = Math.floor(curP);
      // Otherwise use displacement past a modest threshold.
      else if (dx > 0.2) target = Math.ceil(curP);
      else if (dx < -0.2) target = Math.floor(curP);
      else target = Math.round(curP);
      if (settled) goto(target);
    }
    gestureRef.current = {
      pointerId: null, startX: 0, startY: 0, startPos: 0,
      intent: "undecided", lastX: 0, lastT: 0, velocity: 0,
    };
  };

  const onPointerUp = (e) => {
    const g = gestureRef.current;
    if (g.pointerId == null || e.pointerId !== g.pointerId) return;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch {}
    finishGesture(true);
  };

  const onPointerCancel = (e) => {
    const g = gestureRef.current;
    if (g.pointerId == null || e.pointerId !== g.pointerId) return;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch {}
    // If we captured horizontally, settle to the nearest integer so the
    // user doesn't see a partial visual state; if we were still
    // undecided or vertical, cards were never moved so no snap needed.
    if (g.intent === "horizontal") goto(Math.round(position.get()));
    gestureRef.current = {
      pointerId: null, startX: 0, startY: 0, startPos: 0,
      intent: "undecided", lastX: 0, lastT: 0, velocity: 0,
    };
  };

  return (
    <div className="md:hidden -mx-4 mt-10" data-testid="portfolio-cover-flow">
      <div
        ref={containerRef}
        data-testid="portfolio-cover-drag-surface"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        className="relative overflow-hidden select-none"
        style={{
          perspective: 1200,
          height: cardH + 16,
          // pan-y keeps native vertical scrolling responsive while we
          // handle horizontal intent ourselves.
          touchAction: "pan-y",
        }}
      >
        {projects.map((p, i) => (
          <CoverFlowCard
            key={p.id}
            project={p}
            index={i}
            position={position}
            step={step || 1}
            cardW={cardW}
            cardH={cardH}
            N={N}
            media={media}
          />
        ))}
      </div>
      {/* Pagination — one dot per REAL project (not per internal clone).
          Active dot reflects the visually centered card. */}
      <div className="mt-6 flex justify-center items-center gap-2" data-testid="portfolio-cover-pagination">
        {projects.map((_, i) => (
          <button
            key={i}
            type="button"
            data-testid={`portfolio-cover-dot-${i}`}
            aria-label={`Go to project ${i + 1}`}
            aria-current={i === displayActive}
            onClick={() => dotClick(i)}
            className={`h-2 rounded-full transition-all ${
              i === displayActive ? "w-8 bg-[#FF6600]" : "w-2 bg-[#333]"
            }`}
          />
        ))}
        {showPause && (
          <CarouselPauseButton paused={paused} onToggle={onTogglePause} className="ml-3 w-8 h-8" testid="portfolio-cover-pause" />
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------
   Desktop continuous track (Sep 2026, owner-approved architecture).
   One seamless belt: N real cards + N interactive clones, translated
   continuously by a useAnimationFrame loop at TRACK_SPEED px/sec. The
   wrap is done in JS by resetting the x motion value by exactly one
   measured set-width, so the loop point is invisible.
   Clones: fully hoverable/clickable for pointer users, hidden from
   keyboards and screen readers via aria-hidden + tabIndex={-1}
   (owner directive — pointer interaction must never be disabled).
   Desktop branch only; the mobile
   CoverFlow above is completely untouched by this system.
------------------------------------------------------------------ */
const TrackCard = ({ project, cardKey, clone = false, media, onEnter, onLeave }) => (
  <Link
    data-card-key={cardKey}
    to={`/portfolio/${project.slug || project.id}`}
    data-testid={clone ? `project-card-clone-${project.id}` : `project-card-desktop-${project.id}`}
    aria-hidden={clone || undefined}
    tabIndex={clone ? -1 : undefined}
    onPointerEnter={(e) => { if (e.pointerType !== "touch") onEnter(); }}
    onPointerLeave={(e) => { if (e.pointerType !== "touch") onLeave(); }}
    className="ur-card hover:border-[#FF6600]/40 group block fp-card fp-track-card shrink-0"
  >
    <FeaturedCardMedia
      project={project}
      {...media}
      alt={clone ? "" : project.title}
      imgClassName="fp-card-img"
      imgProps={{ loading: "lazy", decoding: "async" }}
      testid={clone ? undefined : `project-card-media-${project.id}`}
    />
    <div className="p-4">
      <div className="font-semibold text-white truncate">{project.title}</div>
      <div className="text-xs text-[#A3A3A3] mt-1 truncate">{project.category}</div>
    </div>
  </Link>
);

const PortfolioTrack = ({ projects, paused, onTogglePause }) => {
  const N = projects.length;
  const reduce = useReducedMotion();
  const viewportRef = useRef(null);
  const setRealRef = useRef(null);
  const setCloneRef = useRef(null);
  const x = useMotionValue(0);
  const speedFactor = useMotionValue(reduce ? 0 : 1);
  const modeRef = useRef("auto"); // "auto" cruise | "manual" arrow step
  const hoverRef = useRef(false);
  const inViewRef = useRef(true);
  const wrapDistRef = useRef(0);
  const unitRef = useRef(0);
  const speedAnimRef = useRef(null);
  const xAnimRef = useRef(null);
  const timersRef = useRef({ leave: null, arrow: null });
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const sectionNear = useInView(viewportRef, { rootMargin: "300px 0px" });
  const sectionVisible = useInView(viewportRef);
  const media = { rootRef: viewportRef, near: sectionNear, visible: sectionVisible, paused };

  // Measure the exact pixel width of ONE set (real cards) — the wrap
  // distance — from the rendered offset between the two set wrappers.
  // Re-measures on resize / layout shifts via ResizeObserver.
  useLayoutEffect(() => {
    const measure = () => {
      const a = setRealRef.current;
      const b = setCloneRef.current;
      if (!a || !b) return;
      const d = b.offsetLeft - a.offsetLeft;
      if (d > 0) {
        wrapDistRef.current = d;
        unitRef.current = d / N;
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (setRealRef.current) ro.observe(setRealRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [N]);

  const rampTo = useCallback((target, ms) => {
    speedAnimRef.current?.stop();
    speedAnimRef.current = animate(speedFactor, target, {
      duration: ms / 1000,
      ease: "easeInOut",
    });
  }, [speedFactor]);

  // Cruise loop — advances x leftward at TRACK_SPEED * speedFactor and
  // wraps by exactly one set-width so the loop point is invisible.
  // Skipped in manual (arrow) mode; delta clamped so returning from an
  // inactive tab never produces a visible jump.
  useAnimationFrame((_, delta) => {
    if (modeRef.current !== "auto") return;
    const v = speedFactor.get();
    const wrapD = wrapDistRef.current;
    if (v <= 0.001 || wrapD <= 0) return;
    const dt = Math.min(delta, 50) / 1000;
    let nx = x.get() - TRACK_SPEED * v * dt;
    while (-nx >= wrapD) nx += wrapD;
    x.set(nx);
  });

  // Off-screen pause — native to the new track (NOT the retired ambient
  // system). When the section leaves the viewport (240px early margin)
  // the cruise ramps to 0 over 300ms; on return it resumes over 600ms.
  // The x position is never reset, so the resume is perfectly continuous.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          inViewRef.current = e.isIntersecting;
          if (modeRef.current !== "auto" || reduce) continue;
          if (e.isIntersecting) {
            if (!hoverRef.current && !pausedRef.current) rampTo(1, 600);
          } else {
            rampTo(0, 300);
          }
        }
      },
      { threshold: 0, rootMargin: "240px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce, rampTo]);

  // Hover: decelerate to a full stop over 400ms so the hovered card can
  // be inspected and clicked. On leave, wait 800ms, then accelerate
  // back to cruise over 600ms. Re-entering any card within the wait
  // window cancels the pending resume.
  const onCardEnter = useCallback(() => {
    hoverRef.current = true;
    const t = timersRef.current;
    if (t.leave) { clearTimeout(t.leave); t.leave = null; }
    if (modeRef.current !== "auto" || !inViewRef.current || reduce) return;
    rampTo(0, 400);
  }, [rampTo, reduce]);

  const onCardLeave = useCallback(() => {
    hoverRef.current = false;
    if (modeRef.current !== "auto" || reduce) return;
    const t = timersRef.current;
    if (t.leave) clearTimeout(t.leave);
    t.leave = setTimeout(() => {
      t.leave = null;
      if (!hoverRef.current && modeRef.current === "auto" && inViewRef.current && !pausedRef.current) {
        rampTo(1, 600);
      }
    }, 800);
  }, [rampTo, reduce]);

  // Arrows: take control, stop the track, advance EXACTLY one card unit
  // over 500ms ease, hold 4 seconds, then resume cruising. x is first
  // normalised into (-wrapD, 0] so the step never crosses the seam
  // mid-animation.
  const resumeCruise = useCallback(() => {
    timersRef.current.arrow = null;
    modeRef.current = "auto";
    if (inViewRef.current && !hoverRef.current && !reduce && !pausedRef.current) rampTo(1, 600);
  }, [reduce, rampTo]);

  const arrowGo = useCallback((dir) => {
    const wrapD = wrapDistRef.current;
    if (wrapD <= 0) return;
    const t = timersRef.current;
    if (t.leave) { clearTimeout(t.leave); t.leave = null; }
    if (t.arrow) { clearTimeout(t.arrow); t.arrow = null; }
    modeRef.current = "manual";
    speedAnimRef.current?.stop();
    speedFactor.set(0);
    xAnimRef.current?.stop();
    let base = x.get() % wrapD;
    if (base > 0) base -= wrapD;
    let target = base - dir * unitRef.current;
    if (target > 0) { base -= wrapD; target -= wrapD; }
    x.set(base);
    const dur = reduce ? 0 : 0.5;
    xAnimRef.current = animate(x, target, { duration: dur, ease: "easeInOut" });
    t.arrow = setTimeout(resumeCruise, dur * 1000 + 4000);
  }, [reduce, speedFactor, x, resumeCruise]);

  // Global pause button: stop/resume the belt smoothly (videos pause via `media.paused`).
  useEffect(() => {
    if (reduce || modeRef.current !== "auto") return;
    if (paused) rampTo(0, 300);
    else if (inViewRef.current && !hoverRef.current) rampTo(1, 600);
  }, [paused, reduce, rampTo]);

  useEffect(() => () => {
    const t = timersRef.current;
    if (t.leave) clearTimeout(t.leave);
    if (t.arrow) clearTimeout(t.arrow);
    speedAnimRef.current?.stop();
    xAnimRef.current?.stop();
  }, []);

  if (N === 0) return null;

  return (
    <div className="hidden md:block relative" data-testid="portfolio-track">
      <div ref={viewportRef} className="fp-viewport" data-testid="portfolio-track-viewport">
        <motion.div className="fp-track" style={{ x }} data-testid="portfolio-track-belt">
          <div ref={setRealRef} className="flex gap-5 shrink-0" data-testid="portfolio-track-set-real">
            {projects.map((p) => (
              <TrackCard
                key={p.id}
                project={p}
                cardKey={`r-${p.id}`}
                media={media}
                onEnter={onCardEnter}
                onLeave={onCardLeave}
              />
            ))}
          </div>
          <div ref={setCloneRef} className="flex gap-5 shrink-0" aria-hidden="true" data-testid="portfolio-track-set-clones">
            {projects.map((p) => (
              <TrackCard
                key={`clone-${p.id}`}
                project={p}
                clone
                cardKey={`c-${p.id}`}
                media={media}
                onEnter={onCardEnter}
                onLeave={onCardLeave}
              />
            ))}
          </div>
        </motion.div>
      </div>
      <button
        data-testid="projects-prev"
        aria-label="Previous projects"
        onClick={() => arrowGo(-1)}
        className="absolute left-[-26px] top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#1A1A1A] border border-[#262626] grid place-items-center hover:border-[#FF6600]"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        data-testid="projects-next"
        aria-label="Next projects"
        onClick={() => arrowGo(1)}
        className="absolute right-[-26px] top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#1A1A1A] border border-[#262626] grid place-items-center hover:border-[#FF6600]"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      {!reduce && (
        <CarouselPauseButton
          paused={paused}
          onToggle={onTogglePause}
          testid="projects-pause"
          className="absolute right-[-20px] top-[calc(100%+14px)] w-9 h-9"
        />
      )}
    </div>
  );
};

export const FeaturedProjects = ({ projects, showDot = false }) => {
  const s = useSettings();
  const ctaLabel = s.featured_projects_cta_label;
  const ctaLink = s.featured_projects_cta_link;
  const eyebrow = s.featured_projects_eyebrow;
  const heading = s.featured_projects_heading;
  const headingAccent = s.featured_projects_heading_accent || "";
  const accentInline = !!s.featured_projects_accent_inline;
  const [paused, togglePause] = useCarouselPause();

  return (
    <section data-testid="featured-projects" className="bg-[#0F0F0F] py-24 relative">
      <div className="max-w-[1400px] min-[1440px]:max-w-[1720px] mx-auto px-4 sm:px-6 lg:px-10 min-[1440px]:px-[clamp(2.5rem,4vw,4rem)]">
        <Reveal className="flex flex-col items-center text-center gap-6">
          <div>
            <div className="ur-eyebrow" data-testid="featured-projects-eyebrow">{eyebrow}</div>
            <h2 data-testid="featured-projects-heading" className="font-display mt-4 font-black text-center text-[clamp(50px,14vw,56px)] leading-[0.95] tracking-[-0.02em] sm:text-6xl sm:leading-[0.95] lg:text-[72px] min-[1440px]:text-[84px]">
              {heading}
              {headingAccent && (
                accentInline
                  ? <span className="text-[#FF6600]">{headingAccent}</span>
                  : <> <span className="text-[#FF6600]">{headingAccent}</span></>
              )}
            </h2>
            {showDot && <div className="mt-4 flex justify-center"><span className="w-2 h-2 rounded-full bg-[#FF6600]" data-testid="featured-projects-dot" /></div>}
          </div>
        </Reveal>

        <div className="mt-12 relative">
          {/* Mobile: 3D Cover Flow — completely untouched by the
              desktop continuous-track rewrite. */}
          <PortfolioCoverFlow projects={projects} paused={paused} onTogglePause={togglePause} />

          {/* Desktop / tablet: seamless continuous horizontal track
              (7 real + 7 interactive clones), no pagination dots. */}
          <PortfolioTrack projects={projects} paused={paused} onTogglePause={togglePause} />
        </div>

        {/* "View Our Portfolio" — moved below the carousel (Sep 2026
            hierarchy pass): centered on every breakpoint, underneath the
            mobile pagination dots / desktop track. Same button, same
            styling, same destination — no duplicate. */}
        <div className="mt-10 flex justify-center">
          <Link
            to={ctaLink}
            data-testid="view-portfolio-link"
            className="ur-btn-secondary text-sm"
          >
            {ctaLabel} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};
